import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "motion/react";
import { Accordion, Select, Button, Pill, Tooltip } from "@foris/avocado-suite";
import { ContextApp } from "@config/Context/contextApp";
import {
  useProgrammingEditStore,
  bulkUpdateStage,
  bulkUpdateInstructors,
  bulkUpdateClassrooms,
} from "@context/useProgrammingEditStore";
import InstructorSelect from "../instructor-select";
import ClassroomSelect from "../classroom-select";
import { InstructorOption } from "../../hooks/useInstructorSearch";
import { ClassroomOption } from "../../hooks/useClassroomSearch";
import styles from "./programmingBulkActions.module.scss";

interface SelectOption {
  value: string;
  label: string;
}

interface ProgrammingBulkActionsProps {
  /** Started link with an applied structure: no bulk edits are possible. */
  isReadOnly?: boolean;
}

const ProgrammingBulkActions: React.FC<ProgrammingBulkActionsProps> = ({ isReadOnly = false }) => {
  const { t } = useTranslation();
  const { user } = useContext(ContextApp);
  const canAssignInstructors = !!user?.abilities?.can_assign_instructors;
  const canAssignClassrooms = !!user?.abilities?.can_assign_classrooms;
  const slots = useProgrammingEditStore(state => state.slots);
  const stages = useProgrammingEditStore(state => state.stages);

  const renderWithPermissionTooltip = (hasPermission: boolean, field: React.ReactElement) => {
    if (hasPermission) return field;

    return (
      <Tooltip
        className={styles.programmingBulkActions_tooltip}
        placement="top"
        label={t("programming-edit.bulk-actions.no-permission")}
      >
        {field}
      </Tooltip>
    );
  };

  const [selectedStage, setSelectedStage] = useState<SelectOption | null>(null);
  const [selectedInstructor, setSelectedInstructor] = useState<InstructorOption | null>(null);
  const [selectedClassroom, setSelectedClassroom] = useState<ClassroomOption | null>(null);

  const selectedCount = useMemo(() => slots.filter(s => s.isSelected).length, [slots]);

  const stageOptions: SelectOption[] = useMemo(
    () => stages.map(s => ({ value: s.id, label: s.name })),
    [stages],
  );

  const handleAssign = () => {
    if (selectedStage) {
      bulkUpdateStage(selectedStage.value);
    }

    if (selectedInstructor) {
      bulkUpdateInstructors([
        { id: selectedInstructor.id, name: selectedInstructor.name, code: selectedInstructor.code },
      ]);
    }

    if (selectedClassroom) {
      bulkUpdateClassrooms([
        {
          id: selectedClassroom.id,
          name: selectedClassroom.name,
          code: selectedClassroom.code,
        },
      ]);
    }

    setSelectedStage(null);
    setSelectedInstructor(null);
    setSelectedClassroom(null);
  };

  const hasSelection = selectedStage || selectedInstructor || selectedClassroom;

  const isVisible = selectedCount >= 2;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className={styles.programmingBulkActions}
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ type: "spring", stiffness: 400, damping: 22, mass: 0.8 }}
        >
          <Accordion
            className={styles.programmingBulkActions_accordion}
            title={t("programming-edit.bulk-actions.title")}
            actionContent={
              <Pill className={styles.programmingBulkActions_pill}>
                {t("programming-edit.bulk-actions.selected-count", { count: selectedCount })}
              </Pill>
            }
            opened
          >
            <div className={styles.programmingBulkActions_panel}>
              <div className={styles.programmingBulkActions_field}>
                <Select
                  label={t("programming-edit.bulk-actions.stage-label")}
                  placeholder={t("programming-edit.bulk-actions.stage-placeholder")}
                  options={stageOptions}
                  value={selectedStage}
                  disabled={isReadOnly}
                  onChange={opt => setSelectedStage((opt as SelectOption) ?? null)}
                  isClearable
                  menuPlacement="top"
                />
              </div>
              {renderWithPermissionTooltip(
                canAssignInstructors,
                <div className={styles.programmingBulkActions_field}>
                  <InstructorSelect
                    label={t("programming-edit.bulk-actions.instructor-label")}
                    placeholder={t("programming-edit.bulk-actions.instructor-placeholder")}
                    value={selectedInstructor}
                    disabled={!canAssignInstructors || isReadOnly}
                    onChange={setSelectedInstructor}
                    isClearable
                    menuPlacement="top"
                  />
                </div>,
              )}
              {renderWithPermissionTooltip(
                canAssignClassrooms,
                <div className={styles.programmingBulkActions_field}>
                  <ClassroomSelect
                    label={t("programming-edit.bulk-actions.classroom-label")}
                    placeholder={t("programming-edit.bulk-actions.classroom-placeholder")}
                    value={selectedClassroom}
                    disabled={!canAssignClassrooms || isReadOnly}
                    onChange={setSelectedClassroom}
                    isClearable
                    menuPlacement="top"
                  />
                </div>,
              )}
              <div className={styles.programmingBulkActions_action}>
                <Button
                  variant="primary"
                  disabled={!hasSelection || isReadOnly}
                  onClick={handleAssign}
                >
                  {t("programming-edit.bulk-actions.assign")}
                </Button>
              </div>
            </div>
          </Accordion>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProgrammingBulkActions;
